import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import PropTypes from "prop-types";
import {
  Avatar,
  Box,
  Button,
  Collapse,
  ListItem,
  makeStyles,
  ListItemIcon,
} from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";

const useStyles = makeStyles((theme) => ({
  item: {
    display: "block",
    paddingTop: 0,
    paddingBottom: 0,
  },
  itemLeaf: {
    display: "flex",
    paddingTop: 0,
    paddingBottom: 0,
    marginTop: "8px", // Increased spacing
    marginBottom: "8px",
  },
  button: {
    color: "#6b7280", // Gray-500
    padding: "12px 16px",
    justifyContent: "flex-start",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
    fontWeight: 500,
  },
  buttonLeaf: {
    color: "#6b7280", // Gray-500
    padding: "12px 16px",
    justifyContent: "flex-start",
    borderRadius: "12px",
    textTransform: "none",
    letterSpacing: 0,
    width: "100%",
    fontSize: "0.875rem",
    fontWeight: 500,
    display: "flex",
    textDecoration: "none",
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      borderRadius: "12px",
      background: "rgba(79, 70, 229, 0.08)", // Soft Indigo Tint
      color: "#4f46e5", // Indigo-600
      "& $icon": {
        color: "#4f46e5",
      },
      "&.depth-0": {
        "& $title": {
          fontWeight: 600,
          color: "#4f46e5",
        },
      },
    },
    "&.depth-0": {
      "& $title": {
        fontWeight: 500,
        color: "inherit",
      },
    },
    "& $icon": {
      color: "#9ca3af", // Gray-400
    },
  },
  icon: {
    display: "flex",
    alignItems: "center",
    marginRight: theme.spacing(2),
    fontSize: "20px",
  },
  title: {
    marginRight: "auto",
  },
  active: {
    borderRadius: "12px",
    fontWeight: 600,
    backgroundColor: "var(--primary-main)", // Matches theme.overrides.MuiButton.containedPrimary.backgroundColor
    color: "#fff",
    boxShadow: "0 4px 12px rgba(109, 169, 210, 0.3)", // Adjusted shadow color
    "&.depth-0": {
      "& $title": {
        fontWeight: 600,
        color: "#fff",
      },
    },
    "& $icon": {
      color: "#fff",
    },
    "&:hover": {
      backgroundColor: "#fff",
      color: "var(--primary-main)",
      border: "1px solid var(--primary-main)",
      "& $icon": {
        color: "var(--primary-main)",
      },
    },
  },
}));

const NavItem = ({
  children,
  className,
  depth,
  href,
  icon: Icon,
  info: Info,
  open: openProp,
  title,
  ...rest
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(openProp);
  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  let paddingLeft = 8;

  if (depth > 0) {
    paddingLeft = 32 + 8 * depth;
  }

  const style = { paddingLeft };

  if (children) {
    return (
      <ListItem
        className={clsx(classes.item, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <Button
          className={classes.button}
          onClick={handleToggle}
          style={{ color: "#ccc" }}
        >
          {Icon && <Icon className={classes.icon} size="20" />}
          <span className={classes.title}>{title}</span>
          {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </Button>
        <Collapse in={open}>{children}</Collapse>
      </ListItem>
    );
  }

  return (
    <>
      <ListItem
        className={clsx(classes.itemLeaf, className)}
        disableGutters
        key={title}
        {...rest}
      >
        <NavLink
          activeClassName={classes.active}
          className={clsx(classes.buttonLeaf, `depth-${depth}`)}
          // component={RouterLink}
          exact
          style={style}
          to={href}
        >
          {Icon && <Icon className={classes.icon} size="20" />}
          <span className={classes.title}>{title}</span>
          {Info && <Info />}
        </NavLink>
      </ListItem>
    </>
  );
};

NavItem.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  depth: PropTypes.number.isRequired,
  href: PropTypes.string,
  icon: PropTypes.elementType,
  info: PropTypes.elementType,
  open: PropTypes.bool,
  title: PropTypes.string.isRequired,
};

NavItem.defaultProps = {
  open: false,
};

export default NavItem;
